/**
 * Compaign 相关 store
 */
import apis from 'src/const/apis';
import * as service from 'src/service';
import request from 'src/utils/request';
import * as types from 'src/store/types';
import { splitTestType } from 'src/const/state/campaign';
import { captureError } from 'src/logger/errorLogger';
import { formatMoney } from 'src/mixins/currencyMixin';
import { DEEP_BID_TYPE } from 'src/const/state/ad';

export default {
  namespaced: true,
  state: {
    // 这里的数据用于 adgroup 获取 campaign 的信息
    campaignSaved: {
      objType: 'Auction',
      campaign_id: '',
      campaign_name: '',
      objective_type: '',
      budget: '',
      dedicate_type: 0,
      budget_mode: 0,
      struct_version: 0,
      industry_types: [],
      ab_test: splitTestType.NONE,
      // CBO 相关入参
      budget_optimize_switch: 0,
      optimize_goal: 0,
      smart_bid_type: 0,
      deep_bid_type: 0,
      roas_bid: 0,
      campaign_app_profile_page_type: undefined,
    },
    campaignSuccess: false,
    campaignListToken: null,
    campaignList: [],
    fakeCampaignId: '',
    campaignSuggestBid: '',
  },
  getters: {
    campaignKey(state, getters, rootState, rootGetter) {
      // 动态计算
      return `${rootGetter['campaign/modelKey']}:campaign`;
    },
    useCBO(state, getters, rootState, rootGetter) {
      // 1 为开启 cbo, 0 默认关闭
      return state.campaignSaved.budget_optimize_switch === 1
        || rootState.campaign.adgroup.adCampaignData.budget_optimize_switch === 1;
    },
    useCBOVO(state, getters, rootState, rootGetter) {
      // cbo vo
      return (state.campaignSaved.budget_optimize_switch === 1
        || rootState.campaign.adgroup.adCampaignData.budget_optimize_switch === 1) &&
        ([DEEP_BID_TYPE.VO_MINIMUN_ROAS, DEEP_BID_TYPE.VO_HIGHEST_VALUE].includes(rootState.campaign.adgroup.adCampaignData.deep_bid_type) ||
        [DEEP_BID_TYPE.VO_MINIMUN_ROAS, DEEP_BID_TYPE.VO_HIGHEST_VALUE].includes(state.campaignSaved.deep_bid_type));
    },
    haveCampaignSuggestBid(state) {
      return Number(state.campaignSuggestBid) !== 0;
    },
    /** 选择的行业类型数组 */
    selectedIndustry(state) {
      return state.campaignSaved?.industry_types ?? [];
    },
    /** 是否选了行业类型 */
    isSelectedIndustry(state) {
      return !!state.campaignSaved?.industry_types?.length;
    },
  },
  mutations: {
    [types.SET_CAMPAIGN](state, data = {}) {
      state.campaignSaved = {
        ...state.campaignSaved,
        ...data,
      };
    },
    [types.SET_FAKE_CAMPAIGN](state, data = '') {
      const isEmptyObject = JSON.stringify(data) === '{}';
      state.fakeCampaignId = isEmptyObject ? '' : data; // buildStone需要的东西
    },
    [types.GET_CAMPAIGNSUCCESS](state, data) {
      state.campaignSuccess = data;
    },
    [types.SET_REQUEST_TOKEN](state, data) {
      state.campaignListToken = data;
    },
    [types.SET_CAMPAIGN_LIST](state, data) {
      state.campaignList = data;
    },
    [types.SET_CAMPAIGN_SUGGEST_BID](state, data) {
      state.campaignSuggestBid = data;
    },
  },
  actions: {
    // 获取 campaign 的详细信息，用来编辑，TODO：这个接口在其他地方也有引用，为了做一出修改所以在这里再引用一次，希望之后以这里为准
    getCampaignDetail(store, data) {
      return request.get(apis.campaign.info, data);
    },
    // Dashboard 用的请求 Campaign List
    // 过滤了未投放的广告 响应更快
    getDashboardCampaignList({ commit, state }, data) {
      // 重复请求时清除之前的请求
      if (state.campaignListToken) {
        state.campaignListToken.cancel();
      }

      const cancelToken = request.axios.CancelToken;
      const source = cancelToken.source();
      commit(types.SET_REQUEST_TOKEN, source);

      return request.post(apis.campaign.dashboardStatisticList, data, {
        cancelToken: source.token,
      });
    },

    // 获取广告组列表
    getCampaignList({ commit, state }, data) {
      // 重复请求时清除之前的请求
      if (state.campaignListToken) {
        state.campaignListToken.cancel();
      }

      const cancelToken = request.axios.CancelToken;
      const source = cancelToken.source();
      commit(types.SET_REQUEST_TOKEN, source);

      return request.post(apis.campaign.newStatisticList, data, {
        cancelToken: source.token,
      });
    },

    // 获取所有 campaign
    getAllCampaign({ commit }, data) {
      // return request.get(apis.campaign.nav_list, data);
      return request.get(apis.campaign.nav_list, data).then(({ data }) => {
        commit(types.SET_CAMPAIGN_LIST, data.data);
      }).catch((e) => {
        commit(types.SET_CAMPAIGN_LIST, []);
      });
    },
    // 修改 toggle 状态
    updateCampaignStatus({ commit }, data) {
      commit(`perf/${types.SET_PERF_OPERATED_ANCHOR}`, {}, { root: true });
      return request.postForm(apis.campaign.update_status, data);
    },

    // campaign
    async createCampaign({ commit }, args = {}) {
      try {
        return await request.post(apis.campaign.create, args);
      } catch (err) {
        captureError(err);
        return Promise.reject(new Error(err?.code));
      }
    },

    emptyCampaignSaved({ commit }) {
      const initCampaign = {
        objType: 'Auction',
        campaign_id: '',
        campaign_name: '',
        objective_type: '',
        budget: '',
        budget_mode: 0,
        buying_type: 1,
        ab_test: splitTestType.NONE,
        budget_optimize_switch: 0,
        optimize_goal: 0,
        smart_bid_type: 0,
        deep_bid_type: 0,
        roas_bid: 0,
      };
      commit(types.SET_CAMPAIGN, initCampaign);
      commit(types.GET_CAMPAIGNSUCCESS, false);
    },

    removeCampaignData({ dispatch, getters, commit }) {
      dispatch('emptyCampaignSaved');
      commit(types.SET_FAKE_CAMPAIGN);
      localStorage.removeItem(getters.campaignKey);
    },

    getCampaignSuggestBid({ commit }, campaignId) {
      return service.getCampaignSuggestBid({
        campaign_id: campaignId,
      }).then(({ data }) => {
        if (data.has_active_ad_group) {
          commit(types.SET_CAMPAIGN_SUGGEST_BID, data ? formatMoney(data.bid) : '');
        } else {
          commit(types.SET_CAMPAIGN_SUGGEST_BID, '');
        }
      }).catch((e) => {
        commit(types.SET_CAMPAIGN_SUGGEST_BID, '');
      });
    },
  },
};
